// src/screens/IngestDashboard.jsx
import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getIngestDashboard, getIngestTimeseries } from "../api/ingestMonitor";
import { THEME, appViewportStyle, screenStyle, btnStyle, panelStyle } from "../ui/theme";
import { Section, Card, Pill, EmptyState, KV } from "../ui/primitives";

import IngestActivityChart from "../layout/components/ingest/IngestActivityChart";

/* =========================
   helpers
========================= */

function fmtInt(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("pt-BR");
}

function fmtNum(x, digits = 1) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)}`;
}

function fmtPct(x, digits = 1) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

function fmtDeltaPct(x, digits = 1) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}%`;
}

function toneFromDelta(delta) {
  const n = Number(delta);
  if (!Number.isFinite(n)) return "muted";
  if (n > 0.25) return "ok";
  if (n < -0.25) return "alert";
  return "warn";
}

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

/* =========================
   Dashboard
========================= */

export default function IngestDashboard() {
  // ✅ tick para mover "Agora" sem refetch
  const [nowTick, setNowTick] = React.useState(() => Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // main dashboard endpoint
  const q = useQuery({
    queryKey: ["ingest", "dashboard"],
    queryFn: () => getIngestDashboard(),
    staleTime: 30_000,
    keepPreviousData: true,
  });

  const yearParams = React.useMemo(() => ({ mode: "created", range: "year", offset: 0 }), []);

  // year timeseries for chart
  const qYear = useQuery({
    queryKey: ["ingest", "timeseries", yearParams],
    queryFn: () => getIngestTimeseries(yearParams),
    staleTime: 60_000,
    keepPreviousData: true,
  });

  const data = q.data;

  // normalize year series
  const yearItemsRaw = qYear.data?.items ?? [];
  const yearItems = useMemo(() => {
    const arr = Array.isArray(yearItemsRaw) ? yearItemsRaw : [];
    return arr
      .map((r) => {
        const ms =
          r?.bucket_start_utc_ms != null
            ? Number(r.bucket_start_utc_ms)
            : r?.bucket_ms != null
            ? Number(r.bucket_ms)
            : null;

        return {
          ...r,
          bucket_start_utc_ms: Number.isFinite(ms) ? ms : null,
          drones_seen_ytd_total:
            r?.drones_seen_ytd_total != null ? Number(r.drones_seen_ytd_total) : null,
          activated_asof: r?.activated_asof != null ? Number(r.activated_asof) : null,
          seen_ytd_pct_of_activated:
            r?.seen_ytd_pct_of_activated != null ? Number(r.seen_ytd_pct_of_activated) : null,
        };
      })
      .filter((r) => Number.isFinite(r.bucket_start_utc_ms))
      .sort((a, b) => a.bucket_start_utc_ms - b.bucket_start_utc_ms);
  }, [yearItemsRaw]);

  // ✅ dados para o IngestActivityChart: bucket_ms + corte no "Agora" (não plota futuro)
  const yearSeriesForChart = useMemo(() => {
    const now = Date.now();
    return (Array.isArray(yearItems) ? yearItems : [])
      .map((r) => ({
        bucket_ms: r.bucket_start_utc_ms, // o chart usa bucket_ms
        drones_seen_ytd_total: r.drones_seen_ytd_total,
        seen_ytd_pct_of_activated: r.seen_ytd_pct_of_activated,
      }))
      .filter((r) => Number.isFinite(r.bucket_ms))
      .filter((r) => r.bucket_ms <= now);
  }, [yearItems]);

  // ✅ janela do ano (para sombrear passado/futuro e posicionar "Agora")
  const yearWindow = useMemo(() => {
    const y = new Date().getFullYear();
    const start = new Date(`${y}-01-01T00:00:00-03:00`).getTime();
    const end = new Date(`${y}-12-31T23:59:59-03:00`).getTime();
    return { start, end };
  }, []);

  /* =========================
     TOP 4 KPI CARDS
  ========================= */

  const mm30Val = useMemo(() => safeNum(data?.card_mm30_seen?.value), [data]);
  const mm30Delta = useMemo(() => safeNum(data?.card_mm30_seen?.delta_pct_vs_yesterday), [data]);
  const mm30Tone = useMemo(() => toneFromDelta(mm30Delta), [mm30Delta]);

  const mm30PctVal = useMemo(() => safeNum(data?.card_mm30_pct_of_activated?.value), [data]);
  const mm30PctDelta = useMemo(
    () => safeNum(data?.card_mm30_pct_of_activated?.delta_pct_vs_yesterday),
    [data]
  );
  const mm30PctTone = useMemo(() => toneFromDelta(mm30PctDelta), [mm30PctDelta]);

  const seenTotalVal = useMemo(() => safeNum(data?.card_seen_total?.value), [data]);
  const seenTotalDelta = useMemo(
    () => safeNum(data?.card_seen_total?.delta_pct_vs_yesterday),
    [data]
  );
  const seenTotalTone = useMemo(() => toneFromDelta(seenTotalDelta), [seenTotalDelta]);

  const pendingVal = useMemo(() => safeNum(data?.card_pending?.value), [data]);
  const pendingDelta = useMemo(() => safeNum(data?.card_pending?.delta_pct_vs_yesterday), [data]);

  // pendentes: cair é bom
  const pendingTone = useMemo(() => {
    const n = Number(pendingDelta);
    if (!Number.isFinite(n)) return "muted";
    if (n < -0.25) return "ok";
    if (n > 0.25) return "alert";
    return "warn";
  }, [pendingDelta]);

  /* =========================
     EVOLUÇÃO (dia/semana/mês + YoY)
  ========================= */

  const dayCur = useMemo(() => safeNum(data?.evolution?.day?.current), [data]);
  const dayPrev = useMemo(() => safeNum(data?.evolution?.day?.previous), [data]);
  const dayPY = useMemo(() => safeNum(data?.evolution?.day?.prev_year), [data]);
  const dayDeltaPrev = useMemo(() => safeNum(data?.evolution?.day?.delta_pct_vs_previous), [data]);
  const dayDeltaPY = useMemo(() => safeNum(data?.evolution?.day?.delta_pct_vs_prev_year), [data]);
  const dayTonePrev = useMemo(() => toneFromDelta(dayDeltaPrev), [dayDeltaPrev]);
  const dayTonePY = useMemo(() => toneFromDelta(dayDeltaPY), [dayDeltaPY]);

  const weekCur = useMemo(() => safeNum(data?.evolution?.week?.current), [data]);
  const weekPrev = useMemo(() => safeNum(data?.evolution?.week?.previous), [data]);
  const weekPY = useMemo(() => safeNum(data?.evolution?.week?.prev_year), [data]);
  const weekDeltaPrev = useMemo(
    () => safeNum(data?.evolution?.week?.delta_pct_vs_previous),
    [data]
  );
  const weekDeltaPY = useMemo(() => safeNum(data?.evolution?.week?.delta_pct_vs_prev_year), [data]);
  const weekTonePrev = useMemo(() => toneFromDelta(weekDeltaPrev), [weekDeltaPrev]);
  const weekTonePY = useMemo(() => toneFromDelta(weekDeltaPY), [weekDeltaPY]);

  const monthCur = useMemo(() => safeNum(data?.evolution?.month?.current), [data]);
  const monthPrev = useMemo(() => safeNum(data?.evolution?.month?.previous), [data]);
  const monthPY = useMemo(() => safeNum(data?.evolution?.month?.prev_year), [data]);
  const monthDeltaPrev = useMemo(
    () => safeNum(data?.evolution?.month?.delta_pct_vs_previous),
    [data]
  );
  const monthDeltaPY = useMemo(
    () => safeNum(data?.evolution?.month?.delta_pct_vs_prev_year),
    [data]
  );
  const monthTonePrev = useMemo(() => toneFromDelta(monthDeltaPrev), [monthDeltaPrev]);
  const monthTonePY = useMemo(() => toneFromDelta(monthDeltaPY), [monthDeltaPY]);

  return (
    <div style={appViewportStyle()}>
      <div style={screenStyle()}>
        <div style={{ maxWidth: 980, margin: "0 auto", display: "grid", gap: 12 }}>
          {/* HEADER */}
          <Section
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <b style={{ fontSize: 14, letterSpacing: 0.4 }}>DASHBOARD • DATA FLOW</b>
              </div>
            }
            subtitle="Visão rápida: base (MM30) • % vs ativados • histórico • pendências"
            
          >
            {q.isError && (
              <div
                style={{
                  marginTop: 2,
                  padding: 10,
                  borderRadius: 14,
                  border: "1px solid rgba(251,113,133,0.28)",
                  background: "rgba(251,113,133,0.12)",
                  color: THEME.text,
                  fontSize: 12,
                }}
              >
                <b style={{ color: THEME.red }}>Erro:</b> {q.error?.message || "Falha ao carregar"}
              </div>
            )}
          </Section>

          {/* LOADING / EMPTY */}
          {q.isLoading && !data ? (
            <Section>
              <div style={{ color: THEME.muted, padding: 12 }}>Carregando dashboard…</div>
            </Section>
          ) : !data ? (
            <Section>
              <EmptyState>Sem dados para mostrar.</EmptyState>
            </Section>
          ) : (
            <>
              {/* KPI grid (4 cards) */}
              <div
                style={{
                  display: "grid",
                  gap: 10,
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                }}
              >
                <Card
                  label="MM30 • Drones vistos"
                  value={mm30Val != null ? fmtNum(mm30Val, 1) : "—"}
                  right={
                    <Pill tone={mm30Tone} title="Variação percentual vs dia anterior (MM30)">
                      {fmtDeltaPct(mm30Delta)}
                    </Pill>
                  }
                />

                <Card
                  label="MM30 / ativados"
                  value={mm30PctVal != null ? fmtPct(mm30PctVal) : "—"}
                  right={
                    <Pill tone={mm30PctTone} title="Variação percentual vs dia anterior (percentual)">
                      {fmtDeltaPct(mm30PctDelta)}
                    </Pill>
                  }
                />

                <Card
                  label="Drones vistos (all time)"
                  value={seenTotalVal != null ? fmtInt(seenTotalVal) : "—"}
                  right={
                    <Pill tone={seenTotalTone} title="Variação percentual vs dia anterior (acumulado)">
                      {fmtDeltaPct(seenTotalDelta)}
                    </Pill>
                  }
                />

                <Card
                  label="Drones pendentes"
                  value={pendingVal != null ? fmtInt(pendingVal) : "—"}
                  right={
                    <Pill
                      tone={pendingTone}
                      title="Variação percentual vs dia anterior (pendentes). Queda é bom."
                    >
                      {fmtDeltaPct(pendingDelta)}
                    </Pill>
                  }
                />
              </div>

{/* TREND CHART */}
              <Section
                title="Tendência no ano"
                subtitle="Acumulado de drones vistos (YTD) e % vs ativados — buckets diários"
              >
                {qYear.isLoading && !qYear.data ? (
                  <div style={{ color: THEME.muted, padding: 12 }}>Carregando série anual…</div>
                ) : qYear.isError ? (
                  <div style={{ color: "rgba(255,120,120,0.9)", padding: 12 }}>
                    Falha ao carregar timeseries anual
                  </div>
                ) : !yearItems.length ? (
                  <EmptyState>Sem pontos no ano corrente.</EmptyState>
                ) : (
                  <>
                    <div style={{ height: 240 }}>
                      <IngestActivityChart
                        theme={THEME}
                        colors={{
                          flightsLine: THEME.chartPrimary,
                          dronesLine: THEME.chartSecondary,
                          flightsBar: THEME.chartPrimary,
                          dronesBar: THEME.chartSecondary,
                        }}
                        range="year"
                        offset={0}
                        nowTick={nowTick}
                        series={yearSeriesForChart}
                        windowStartMs={yearWindow.start}
                        windowEndMs={yearWindow.end}
                        seriesA={{
                          key: "drones_seen_ytd_total",
                          name: "Vistos YTD",
                          variant: "line",
                          axis: "left",
                          color: THEME.chartPrimary,
                          showDots: false,
                        }}
                        seriesB={{
                          key: "seen_ytd_pct_of_activated",
                          name: "% vs ativados",
                          variant: "line",
                          axis: "right",
                          color: THEME.chartSecondary,
                          showDots: false,
                        }}
                        hideYTicks={true}
                        hideRightAxis={true}
                      />
                    </div>

                    <div
                      style={{
                        marginTop: 10,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 10,
                      }}
                    >
                      {/* espaço reservado p/ cards extras abaixo do chart */}
                    </div>
                  </>
                )}
              </Section>

              {/* EVOLUÇÃO */}
              <Section
                title="Evolução no período"
                subtitle="Comparativos canônicos BR: anterior (↔) e ano anterior (YoY)"
              >
                <div style={{ display: "grid", gap: 10 }}>
                  {/* Dia */}
                  <div style={panelStyle({ padding: 12 })}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 800 }}>Hoje</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Pill tone={dayTonePrev} title="Hoje vs ontem">
                          {fmtDeltaPct(dayDeltaPrev)} vs ontem
                        </Pill>
                        <Pill tone={dayTonePY} title="Hoje vs mesmo dia ano anterior">
                          {fmtDeltaPct(dayDeltaPY)} YoY
                        </Pill>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 10,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 10,
                      }}
                    >
                      <KV label="Hoje" value={dayCur != null ? fmtInt(dayCur) : "—"} />
                      <KV label="Ontem" value={dayPrev != null ? fmtInt(dayPrev) : "—"} />
                      <KV label="Ano anterior" value={dayPY != null ? fmtInt(dayPY) : "—"} />
                    </div>
                  </div>

                  {/* Semana */}
                  <div style={panelStyle({ padding: 12 })}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 800 }}>Esta semana</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Pill tone={weekTonePrev} title="Esta semana vs semana passada">
                          {fmtDeltaPct(weekDeltaPrev)} vs semana passada
                        </Pill>
                        <Pill tone={weekTonePY} title="Esta semana vs mesma semana ano anterior">
                          {fmtDeltaPct(weekDeltaPY)} YoY
                        </Pill>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 10,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 10,
                      }}
                    >
                      <KV label="Esta semana" value={weekCur != null ? fmtInt(weekCur) : "—"} />
                      <KV
                        label="Semana passada"
                        value={weekPrev != null ? fmtInt(weekPrev) : "—"}
                      />
                      <KV label="Ano anterior" value={weekPY != null ? fmtInt(weekPY) : "—"} />
                    </div>
                  </div>

                  {/* Mês */}
                  <div style={panelStyle({ padding: 12 })}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 800 }}>Este mês</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Pill tone={monthTonePrev} title="Este mês vs mês passado">
                          {fmtDeltaPct(monthDeltaPrev)} vs mês passado
                        </Pill>
                        <Pill tone={monthTonePY} title="Este mês vs mesmo mês ano anterior">
                          {fmtDeltaPct(monthDeltaPY)} YoY
                        </Pill>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 10,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 10,
                      }}
                    >
                      <KV label="Este mês" value={monthCur != null ? fmtInt(monthCur) : "—"} />
                      <KV label="Mês passado" value={monthPrev != null ? fmtInt(monthPrev) : "—"} />
                      <KV label="Ano anterior" value={monthPY != null ? fmtInt(monthPY) : "—"} />
                    </div>
                  </div>
                </div>
              </Section>

              
            </>
          )}
        </div>
      </div>
    </div>
  );
}
